"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import "./AiReadyWidget.css";

export default function AiReadyWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    companyName: "",
    brief: "",
    nda: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const apiUrl = (process.env.NEXT_PUBLIC_API_URL || process.env.NEXT_PUBLIC_API_BASE_URL || '').replace(/\/api\/?$/, '').replace(/\/$/, '') || "http://localhost:5001";
      const payload = {
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        companyName: formData.companyName,
        message: formData.brief || "ERP Assessment Request",
      };

      const res = await fetch(`${apiUrl}/api/leads/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setIsSubmitting(false);
        setIsSuccess(true);
        
        // Reset after success message
        setTimeout(() => {
          setIsOpen(false);
          setIsSuccess(false);
          setFormData({
            fullName: "",
            phone: "",
            email: "",
            companyName: "",
            brief: "",
            nda: false,
          });
        }, 3000);
      } else {
        const data = await res.json();
        console.error("Error submitting lead:", data.message);
        setIsSubmitting(false);
        alert(data.message || "Failed to submit. Please try again.");
      }
    } catch (err) {
      console.error("Error submitting lead:", err);
      setIsSubmitting(false);
      alert("Something went wrong. Please try again.");
    }
  };

  return (
    <>
      {/* Sticky Button */}
      <div 
        className="ai-ready-sticky-btn" 
        onClick={() => setIsOpen(true)}
        title="Get your ERP Readiness Assessment"
      >
        <i className="fa-solid fa-chart-line ai-ready-icon"></i>
        <div className="ai-ready-text">Is Your Business ERP-Ready?</div>
      </div>

      {/* Modal Overlay */}
      <div className={`ai-ready-modal-overlay ${isOpen ? "open" : ""}`} onClick={() => setIsOpen(false)}>
        
        {/* Modal Container */}
        <div className="ai-ready-modal-container" onClick={(e) => e.stopPropagation()}>

          {/* Left Panel (Dark) */}
          <div className="ai-modal-left">
            <h2>Your ERP Implementation Partner</h2>
            
            <div className="ai-features-grid">
              <div className="ai-feature-item">
                <h3><i className="fa-solid fa-chart-bar"></i> Is Your Business Ready?</h3>
                <p>We analyze your setup to find where ERP streamlines operations. Get a clear roadmap and save money.</p>
              </div>
              
              <div className="ai-feature-item">
                <h3><i className="fa-solid fa-gear"></i> Automate Operations</h3>
                <p>Stop manual work. Handle inventory, HR, accounting, and sales all in one unified platform.</p>
              </div>
              
              <div className="ai-feature-item">
                <h3><i className="fa-solid fa-database"></i> Data-Driven Growth</h3>
                <p>Turn messy data into a single source of truth. Make confident, real-time business decisions.</p>
              </div>
              
              <div className="ai-feature-item">
                <h3><i className="fa-solid fa-shield-halved"></i> Safe & Scalable</h3>
                <p>Custom ERP solutions that are 100% secure, easy to adopt, and scale as your business grows.</p>
              </div>
            </div>

            <div className="ai-robot-image">
              <Image 
                src="/assets/img/ai-robot-illustration-v4.png" 
                alt="AI Robot with UI Cards"
                width={400}
                height={400}
                style={{ width: "100%", height: "auto" }}
              />
            </div>
          </div>

          {/* Right Panel (Form) */}
          <div className="ai-modal-right">
            <button className="ai-ready-close-btn" onClick={() => setIsOpen(false)}>
              <i className="fa-solid fa-xmark"></i>
            </button>
            
            <h2>Let's Build Your ERP Solution</h2>
            <p className="ai-modal-subtitle">
              Improve efficiency and automate workflows. Get a personalized ERP assessment today.
            </p>

            {isSuccess ? (
              <div className="alert alert-success mt-3" role="alert">
                Thank you! We've received your request. Our team will contact you shortly for your ERP readiness assessment.
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="ai-form-grid">
                  <div className="ai-input-group">
                    <input 
                      type="text" 
                      name="fullName"
                      placeholder="Full Name*" 
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required 
                    />
                  </div>
                  <div className="ai-input-group">
                    <input 
                      type="tel" 
                      name="phone"
                      placeholder="Phone Number*" 
                      value={formData.phone}
                      onChange={handleInputChange}
                      required 
                    />
                  </div>
                  <div className="ai-input-group">
                    <input 
                      type="email" 
                      name="email"
                      placeholder="Company Email*" 
                      value={formData.email}
                      onChange={handleInputChange}
                      required 
                    />
                  </div>
                  <div className="ai-input-group">
                    <input 
                      type="text" 
                      name="companyName"
                      placeholder="Company Name" 
                      value={formData.companyName}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="ai-input-group full-width">
                    <textarea 
                      name="brief"
                      placeholder="Project Brief" 
                      value={formData.brief}
                      onChange={handleInputChange}
                    ></textarea>
                  </div>
                </div>

                <div className="ai-checkbox-group">
                  <input 
                    type="checkbox" 
                    id="nda-check" 
                    name="nda"
                    checked={formData.nda}
                    onChange={handleInputChange}
                  />
                  <label htmlFor="nda-check">I want to protect my data by signing an NDA.</label>
                </div>

                <button type="submit" className="ai-submit-btn" disabled={isSubmitting}>
                  {isSubmitting ? "Submitting..." : "BOOK A FREE CONSULTATION"}
                </button>

                <div className="ai-privacy-text">
                  By submitting this form, you agree to CodeQlik <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                </div>

                <div className="ai-trust-badges" style={{ marginTop: "20px", textAlign: "center" }}>
                  <Image 
                    src="/assets/img/partnerside.png" 
                    alt="Trust Badges" 
                    width={400}
                    height={80}
                    style={{ maxWidth: "100%", height: "auto", width: "auto" }} 
                  />
                </div>
              </form>
            )}
          </div>
          
        </div>
      </div>
    </>
  );
}
